import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { setSelectedCompany } from '../../../store/AuthReducer';
import { useAppDispatch } from '../../../store/hook';
import type { RootState } from '../../../store/store';
import { JWTHeader } from '../../../utils/JWTHeader';
import { ROUTES } from '../../../utils/routes';
import Badge from '../../common/Badge';
import { showToast } from '../../common/Notification';
import Spinner from '../../common/Spinner';
import Table from '../../common/Table';

const sortableColumns = {
  NOM: 'string', SIREN: 'string', 'COPROS À VÉRIFIER': 'number',
};

function CoproCompanyValidation() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const jwt = useSelector((state: RootState) => state.auth.token);
  const company = useSelector((state: RootState) => state.auth.company);
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('IDLE');

  const handleCompanySelect = async(companyId: number) => {
    try {
      const response = await fetch(`/api/companies/${companyId}`, JWTHeader(jwt));

      if (!response.ok) {
        throw new Error('Error fetching company data');
      }

      const companyData = await response.json();
      dispatch(setSelectedCompany(companyData));
      navigate(ROUTES.ADMIN_COPRO_VALIDATION);
    } catch (error) {
      showToast('error', 'Une erreur est survenue lors du chargement des données.');
      console.error('Error fetching company data :', error);
    }
  };

  const fetchData = async() => {
    setStatus('PENDING');
    try {
      const response = await fetch('/api/companies/copros-to-verify', JWTHeader(jwt));
      const result = await response.json();

      const formattedData = result.map((item) => ({
        ID: item.id,
        NOM: item.name,
        SIREN: item.siren,
        'COPROS À VÉRIFIER': item.coprosToVerify,
        ACTIONS: (
          <div className="p-2">
            <button
              onClick={() => handleCompanySelect(item.id)}
              className="align-center w-full rounded bg-rgpd-blue-dark px-5 py-3 text-base whitespace-nowrap text-white shadow-md md:w-auto"
            >
              Vérifier
            </button>
          </div>
        ),
      }));

      setData(formattedData);
      setStatus('SUCCESS');
    } catch (error) {
      console.error('Error fetching data :', error);
      showToast('error', 'Une erreur est survenue lors du chargement des données.');
      setStatus('ERROR');
    }
  };

  useEffect(() => {
    if (jwt && company && company.id && status !== 'PENDING') {
      fetchData();
    }
  }, [jwt, company]);

  return (
    <div>
      <div className="mb-8 overflow-x-scroll rounded-lg bg-white p-4 shadow-md">
        {status === 'PENDING' && (
          <p className="mx-10 my-32 text-center">
            <Spinner aria-label="Center-aligned spinner" size="xl" />
          </p>
        )}
        {status === 'SUCCESS' && (
          <>
            <Table
              data={data}
              headers={['NOM', 'SIREN', 'COPROS À VÉRIFIER', 'ACTIONS']}
              sortableColumns={sortableColumns}
              tableClass="text-sm text-center"
              isSearchable
              idKey="SIREN"
            />
            {data.length === 0 && (
              <div className="flex w-full place-content-center">
                <Badge
                  mode="warning"
                  text={'Aucune entreprise n\'a de copro à vérifier'}
                  className="mt-8"
                />
              </div>
            )}
          </>
        )}
        {status === 'ERROR' && (
          <div className="flex w-full place-content-center">
            <Badge
              mode="error"
              text="Erreur lors du chargement des données"
            />
          </div>
        )}
      </div>
    </div>
  );
}

export default CoproCompanyValidation;
